import {useMutation} from "@tanstack/react-query";
import {isAxiosError} from "axios";
import {useCallback, useState} from "react";
import {toast} from "sonner";

import {TOAST_CONFIG} from "@/lib/constansts";
import {getAxiosErrorMessage} from "@/src/modules/core/lib/tanstack-error";

import {HOUSEHOLD_ASSIST_RATE_LIMIT_COOLDOWN_MS} from "../lib/household-assist";
import {householdAssistService} from "../services/household-assist";
import type {HouseholdAssistResult} from "../types/household-assist";

export function useHouseholdAssistSearch() {
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isRateLimited = rateLimitedUntil != null && Date.now() < rateLimitedUntil;

  const mutation = useMutation({
    mutationFn: (q: string) => householdAssistService.search(q),
    retry: false,
    onMutate: () => {
      setValidationError(null);
    },
    onError: (error) => {
      if (isAxiosError(error) && error.response?.status === 429) {
        setRateLimitedUntil(Date.now() + HOUSEHOLD_ASSIST_RATE_LIMIT_COOLDOWN_MS);
        toast.error("Too many searches. Wait a moment and try again.", TOAST_CONFIG);
        return;
      }

      if (isAxiosError(error) && error.response?.status === 422) {
        const [, errors] = getAxiosErrorMessage(error);
        setValidationError(errors[0] ?? "Enter at least 2 characters to search.");
        return;
      }

      const [message] = isAxiosError(error) ? getAxiosErrorMessage(error) : [error.message, []];
      if (message) {
        toast.error(message, TOAST_CONFIG);
      }
    },
  });

  const search = useCallback(
    async (q: string): Promise<HouseholdAssistResult | null> => {
      if (isRateLimited) return null;
      try {
        return await mutation.mutateAsync(q);
      } catch {
        return null;
      }
    },
    [isRateLimited, mutation],
  );

  return {
    search,
    data: mutation.data ?? null,
    isPending: mutation.isPending,
    validationError,
    isRateLimited,
    reset: mutation.reset,
  };
}
